import React, { useState } from 'react';
import { array, bool, func, string } from 'prop-types';
import { useDispatch } from 'react-redux';
import PQueue from 'p-queue';

import ConfirmActionModal from './ConfirmActionModal';
import ProfileObjectModificationModal from './ProfileObjectModificationModal';
import ProfileObjectModificationResultModal from './ProfileObjectModificationResultModal';

import {
    deleteProfileObject,
    fetchProfileObjects
} from '../../actions/profileObjectActions';
import { enqueueSnackbar } from '../../actions/applicationMessageActions';

const DeleteProfileObjectModal = ({
    profileObjects,
    onClose,
    open,
    profileId,
    profileName
}) => {
    const dispatch = useDispatch();
    const [requestErrors, setRequestErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [completedObjects, setCompletedObjects] = useState(0);
    const [pendingObjects, setPendingObjects] = useState(0);

    function handleClose() {
        onClose();
    }

    async function handleConfirm() {
        setIsSubmitting(true);
        const queue = new PQueue({ concurrency: 4 });
        const results = [];
        profileObjects.forEach((profileObject) => {
            queue.add(async () => {
                try {
                    await dispatch(deleteProfileObject(profileObject));
                    results.push({
                        item:   profileObject,
                        status: 'fulfilled',
                        value:  profileObject
                    });
                } catch (error) {
                    results.push({
                        item:   profileObject,
                        status: 'rejected',
                        error
                    });
                }
            });
        });
        queue.on('active', () => {
            setCompletedObjects(profileObjects.length - queue.size);
            setPendingObjects(queue.pending);
        });
        await queue.onIdle();
        const resultErrors = results.filter(
            ({ status }) => status === 'rejected'
        );
        setIsSubmitting(false);
        setRequestErrors(resultErrors);
        onClose();
        if (!resultErrors.length) {
            dispatch(enqueueSnackbar('Profile Object(s) deleted successfully', 'success'));
            dispatch(fetchProfileObjects(profileId));
        }
    }

    function handleResultClose() {
        setRequestErrors([]);
    }

    const hasErrors = requestErrors.length > 0;
    const showConfirmModal = open && !isSubmitting;

    return (
        <>
            <ConfirmActionModal
                imgSrc="/assets/images/profilesIconColor.svg"
                onClose={handleClose}
                onConfirm={handleConfirm}
                open={showConfirmModal}
                title={`Delete ${profileObjects.length} Profile Object(s)`}
                confirmationLabel="Delete Confirmation"
            >
                <p>
                    Deleting a Profile Object will update Objects for all Devices that currently use this Profile: {profileName}.
                </p>
                <p>
                    Are you sure you want to delete these Profiles Objects:
                </p>
                <ul>
                    {profileObjects.map(({ id, name }) => (
                        <li key={id}>
                            {name}
                        </li>
                    ))}
                </ul>
            </ConfirmActionModal>
            <ProfileObjectModificationModal
                completed={completedObjects}
                modificationType="delete"
                open={isSubmitting}
                pending={pendingObjects}
            />
            <ProfileObjectModificationResultModal
                errors={requestErrors}
                modificationType="delete"
                onClose={handleResultClose}
                open={hasErrors}
            />
        </>
    );
};

DeleteProfileObjectModal.defaultProps = {
    onClose: () => {},
    open:    false
};

DeleteProfileObjectModal.propTypes = {
    profileObjects: array.isRequired,
    onClose:        func,
    open:           bool,
    profileId:      string.isRequired,
    profileName:    string
};

export default DeleteProfileObjectModal;
